import { useState, useEffect } from "react";
import { ArrowRight, Mail, Phone, MapPin, Check } from "lucide-react";
import Container from "../ui/Container";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formRateLimiter } from "@/lib/utils/rateLimit";
import { 
  validateContactForm, 
  type ContactFormData,
  type ValidationResult,
  type ValidationFailure 
} from "@/lib/utils/formValidation";

const isValidationFailure = (result: ValidationResult): result is ValidationFailure => {
  return !result.success;
};

const Contact = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ field: string; message: string }[]>([]);
  const [remainingTime, setRemainingTime] = useState(0);

  useEffect(() => {
    // Check remaining time on component mount
    const time = formRateLimiter.getRemainingTime("contact");
    if (time > 0) {
      setRemainingTime(time);
      const timer = setInterval(() => {
        setRemainingTime(prev => Math.max(0, prev - 1000));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear validation error for the field being changed
    setValidationErrors(prev => prev.filter(error => error.field !== name));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check rate limiting
    if (!formRateLimiter.canSubmit("contact")) {
      const time = formRateLimiter.getRemainingTime("contact");
      setRemainingTime(time);
      toast.error(`Пожалуйста, подождите ${Math.ceil(time / 1000)} секунд перед повторной отправкой`);
      return;
    }

    // Validate form data
    const validationResult = validateContactForm(formData);
    if (isValidationFailure(validationResult)) {
      setValidationErrors(validationResult.errors);
      toast.error("Пожалуйста, исправьте ошибки в форме");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await api.contact.sendMessage(validationResult.data as ContactFormData);
      toast.success("Ваше сообщение успешно отправлено! Мы свяжемся с вами в ближайшее время.");
      setIsSubmitted(true);
      setFormData({ name: "", email: "", phone: "", company: "", message: "" });
      
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast.error(error.message || "Произошла ошибка при отправке сообщения. Пожалуйста, попробуйте позже.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="h-5 w-5 text-primary" />,
      title: "Телефон",
      details: "+375 (29) 332-60-12",
      link: "tel:+375293326012"
    },
    {
      icon: <Mail className="h-5 w-5 text-primary" />,
      title: "Email",
      details: "info@stackadvance.com",
      link: "mailto:info@stackadvance.com"
    },
    {
      icon: <MapPin className="h-5 w-5 text-primary" />,
      title: "Адрес",
      details: "г. Минск, ул. Кульман, д. 9",
      link: "https://yandex.by/maps/org/stack_advance/93373093742/?ll=27.580094%2C53.920990&z=17"
    }
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute left-0 top-1/3 w-1/3 h-1/3 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute right-0 bottom-1/3 w-1/3 h-1/3 bg-accent/5 rounded-full blur-3xl"></div>
      </div>

      <Container>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="tag mb-4 mx-auto inline-block">Свяжитесь с нами</div>
          <h2 className="heading text-3xl md:text-4xl font-bold mb-6">
            Готовы обсудить <span className="text-gradient">ваш проект</span>?
          </h2>
          <p className="text-muted-foreground text-lg">
            Напишите нам, и мы свяжемся с вами, чтобы обсудить, как мы можем помочь вашему бизнесу с помощью инновационных технологических решений.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {contactInfo.map((item, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-5 border border-border flex items-start"
              >
                <div className="mr-4 mt-1">{item.icon}</div>
                <div>
                  <h3 className="text-base font-semibold mb-1">{item.title}</h3>
                  <a 
                    href={item.link} target="_blank"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {item.details}
                  </a>
                </div>
              </div>
            ))}
            
            <div className="bg-white rounded-xl p-5 border border-border">
              <h3 className="text-base font-semibold mb-4">Время работы</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Понедельник - Пятница:</span>
                  <span>9:00 - 18:00</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Суббота - Воскресенье:</span>
                  <span>Выходной</span>
                </div>
              </div>
            </div>
            
            <div className="hidden lg:block bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6">
              <div className="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-primary mb-4">
                  <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2v5Z"></path>
                  <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"></path>
                </svg>
                <h3 className="text-lg font-semibold mb-2">Нужна срочная консультация?</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Позвоните нам, и мы ответим на все ваши вопросы прямо сейчас.
                </p>
                <a 
                  href="tel:+375293326012" 
                  className="button-primary w-full justify-center"
                >
                  Позвонить
                </a>
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-3">
            <div className="glass rounded-2xl p-6 md:p-8">
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Сообщение отправлено!</h3>
                  <p className="text-muted-foreground mb-6">
                    Спасибо за обращение. Мы свяжемся с вами в ближайшее время.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2">
                        Имя*
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className={`w-full px-4 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                          validationErrors.some(e => e.field === "name") ? "border-red-500" : "border-border"
                        }`}
                        placeholder="Введите ваше имя"
                      />
                      {validationErrors.some(e => e.field === "name") && (
                        <p className="mt-1 text-sm text-red-500">
                          {validationErrors.find(e => e.field === "name")?.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        Email*
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={`w-full px-4 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                          validationErrors.some(e => e.field === "email") ? "border-red-500" : "border-border"
                        }`}
                        placeholder="Введите ваш email"
                      />
                      {validationErrors.some(e => e.field === "email") && (
                        <p className="mt-1 text-sm text-red-500">
                          {validationErrors.find(e => e.field === "email")?.message}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2">
                        Телефон
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                          validationErrors.some(e => e.field === "phone") ? "border-red-500" : "border-border"
                        }`}
                        placeholder="Введите ваш телефон"
                      />
                      {validationErrors.some(e => e.field === "phone") && (
                        <p className="mt-1 text-sm text-red-500">
                          {validationErrors.find(e => e.field === "phone")?.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium mb-2">
                        Компания
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                          validationErrors.some(e => e.field === "company") ? "border-red-500" : "border-border"
                        }`}
                        placeholder="Введите название компании"
                      />
                      {validationErrors.some(e => e.field === "company") && (
                        <p className="mt-1 text-sm text-red-500">
                          {validationErrors.find(e => e.field === "company")?.message}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Сообщение*
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className={`w-full px-4 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none ${
                        validationErrors.some(e => e.field === "message") ? "border-red-500" : "border-border"
                      }`}
                      placeholder="Расскажите о вашем проекте или вопросе"
                    ></textarea>
                    {validationErrors.some(e => e.field === "message") && (
                      <p className="mt-1 text-sm text-red-500">
                        {validationErrors.find(e => e.field === "message")?.message}
                      </p>
                    )}
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting || remainingTime > 0}
                    className="button-primary w-full justify-center py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Отправка...
                      </span>
                    ) : remainingTime > 0 ? (
                      <span className="flex items-center">
                        Подождите {Math.ceil(remainingTime / 1000)} сек
                      </span>
                    ) : (
                      <span className="flex items-center">
                        Отправить сообщение
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </span>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Contact;
